import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, X, Save, ArrowLeft } from 'lucide-react';
import { interDepotService } from '../../../services/interDepotService';
import SearchSelect from '../../../components/SearchSelect';
import MantleDataGrid, { gcell } from '../../../components/MantleDataGrid';
import { useColumnFilters, FilterPopover } from '../../../components/gridFilters';
import PickerPopover from '../../../components/PickerPopover';
import { isAdmin } from '../../../utils/permissions';
import { useAppContext } from '../../../context/AppContext';

// =============================================================================
// Budget Setup > Inter-Depot Posting  (Distribution)
//
// Where inter-depot transport lands in the ledger. Decided ONCE by the budget
// owner rather than by whoever happens to be entering rates — the same split
// that makes Variable Expenses a catalogue: deciding that haulage is GL 6300 is
// a chart-of-accounts decision with one owner, and leaving it on the capture
// screen is how six depots post the same movement to four accounts.
//
// SCOPED, MOST SPECIFIC WINS:
//
//     Site                       the default for everything leaving that site
//     Site + lane                everything down one lane
//     Site + carrier type        own-fleet legs vs contracted legs
//     Site + lane + carrier      the narrowest rule
//
// Site is the lane's ORIGIN — the depot that dispatches is the one that pays,
// which is the same rule the rating page enforces.
//
// Version-scoped, because a chart of accounts belongs to a budget. The
// contractor master next door is not, because a carrier is a fact about the
// world rather than about a scenario.
//
// ON THE MANTLE GRID, natively, following People › GL Mapping — the same
// treatment as its sibling, Secondary Posting. A catalogue is read far more
// often than it is written; at forty lanes it needs header filters and Ctrl+F
// rather than a page of dropdowns. Account and cost centre are PICKED from a
// popover, never typed.
//
// The ADD line stays an ordinary form above the grid: a new rule needs a site
// and a scope chosen together before it means anything, and a canvas grid is a
// poor place to assemble a composite key one cell at a time.
// =============================================================================

const CARRIER_OPTIONS = [
  { code: '', label: 'Any carrier' },
  { code: 'OWNED', label: 'Own fleet only' },
  { code: 'CONTRACTED', label: 'Contracted only' },
];
const CARRIER_LABEL = { OWNED: 'Own fleet', CONTRACTED: 'Contracted' };

const BLANK = { site_code: '', lane_id: '', carrier_type: '', account_code: '', cost_center_code: '', notes: '' };

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const field = 'w-full text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-40';

// How specific a rule is, for display and for sorting. Mirrors the ORDER BY in
// vw_bud_transfer_cost — if these two ever disagree, the page is lying about
// which rule applies.
const specificity = (r) => (r.lane_id ? 2 : 0) + (r.carrier_type ? 1 : 0);

const MUTED = { textDark: '#94a3b8' };
const SCOPED = { textDark: '#2563eb' };
const MISSING = { textDark: '#dc2626' };

export default function TransferPosting() {
  const navigate = useNavigate();
  const { selectedRole, selectedVersion } = useAppContext();
  const canEdit = isAdmin(selectedRole);

  const [rules, setRules] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [picks, setPicks] = useState({ sites: [], accounts: [], costCenters: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [dirty, setDirty] = useState({});
  const [entry, setEntry] = useState(BLANK);
  const [adding, setAdding] = useState(false);
  const [picker, setPicker] = useState(null);   // { id, field, x, y }
  const [filterUI, setFilterUI] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await interDepotService.getPosting();
      const d = res.data || {};
      setRules(d.rules || []);
      setLanes(d.lanes || []);
      setPicks({ sites: d.sites || [], accounts: d.accounts || [], costCenters: d.costCenters || [] });
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load posting rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Broadest first, so the page reads the way resolution works: a site default
  // at the top, the exceptions that beat it underneath.
  const sorted = useMemo(
    () => [...rules].sort((a, b) => a.site_code.localeCompare(b.site_code) || specificity(a) - specificity(b)),
    [rules]
  );

  const lanesForSite = useCallback(
    (site) => lanes.filter((l) => !site || l.origin_site_code === site),
    [lanes]
  );

  // Sites with no rule at all: every lane out of them posts nowhere.
  const uncovered = useMemo(() => {
    const covered = new Set(rules.map((r) => r.site_code));
    const origins = new Set(lanes.map((l) => l.origin_site_code));
    return [...origins].filter((s) => !covered.has(s));
  }, [rules, lanes]);

  const patch = useCallback((id, p) => {
    setRules((prev) => prev.map((r) => (r.posting_id === id ? { ...r, ...p } : r)));
    setDirty((d) => ({ ...d, [id]: true }));
  }, []);

  const scopeLabel = useCallback((r) => {
    if (!r.lane_id && !r.carrier_type) return 'Site default';
    const parts = [];
    if (r.lane_id) parts.push(`${r.origin_site_code} → ${r.dest_site_code}`);
    if (r.carrier_type) parts.push(CARRIER_LABEL[r.carrier_type]);
    return parts.join(' · ');
  }, []);

  const accLabel = useCallback((code) => {
    if (!code) return '';
    const a = picks.accounts.find((x) => x.code === code);
    return a ? `${code} · ${a.description}` : code;
  }, [picks.accounts]);
  const ccLabel = useCallback((code) => {
    if (!code) return '';
    const c = picks.costCenters.find((x) => x.code === code);
    return c ? `${code} · ${c.description}` : code;
  }, [picks.costCenters]);

  // ---- Filtering -------------------------------------------------------------
  const accessors = useMemo(() => ({
    site: (r) => `${r.site_code} · ${r.site_name || ''}`.trim(),
    scope: (r) => scopeLabel(r),
    account: (r) => accLabel(r.account_code),
    cost_center: (r) => ccLabel(r.cost_center_code),
  }), [scopeLabel, accLabel, ccLabel]);

  const { setFilter, filters, clearAll, filtered, activeIds } = useColumnFilters(sorted, accessors);

  const filteredRef = useRef([]);
  filteredRef.current = filtered;

  // ---- Grid ------------------------------------------------------------------
  const gcols = useMemo(() => {
    const dot = (id, title) => (activeIds.includes(id) ? `${title} ●` : title);
    return [
      { id: 'site', title: dot('site', 'Site'), width: 190, hasMenu: true },
      { id: 'scope', title: dot('scope', 'Applies To'), width: 220, hasMenu: true },
      { id: 'account', title: dot('account', 'GL Account'), width: 260, grow: 1, hasMenu: true },
      { id: 'cost_center', title: dot('cost_center', 'Cost Centre'), width: 220, grow: 1, hasMenu: true },
      { id: 'notes', title: 'Notes', width: 220 },
      { id: 'del', title: '', width: 40 },
    ];
  }, [activeIds]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = filtered[row];
    if (!r) return gcell.text('', { readonly: true });
    const edited = !!dirty[r.posting_id];
    switch (id) {
      case 'site':
        return gcell.text(`${edited ? '• ' : ''}${r.site_code}${r.site_name ? ` · ${r.site_name}` : ''}`, { readonly: true });
      // A site default reads grey, a narrowing rule blue: the page's whole job
      // is making "which one wins" legible at a glance.
      case 'scope':
        return gcell.text(scopeLabel(r), { readonly: true, theme: specificity(r) === 0 ? MUTED : SCOPED });
      case 'account':
        return gcell.text(accLabel(r.account_code) || '— none —',
          { readonly: true, theme: r.account_code ? undefined : MISSING });
      case 'cost_center':
        return gcell.text(ccLabel(r.cost_center_code) || 'none', { readonly: true, theme: r.cost_center_code ? undefined : MUTED });
      case 'notes': return gcell.text(r.notes || '', { readonly: !canEdit });
      case 'del': return gcell.text(canEdit ? '✕' : '', { readonly: true, theme: MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, [colIds, filtered, dirty, scopeLabel, accLabel, ccLabel, canEdit]);

  const onCellEdited = useCallback(([col, row], newValue) => {
    if (colIds[col] !== 'notes') return;
    const r = filteredRef.current[row];
    if (r) patch(r.posting_id, { notes: String(newValue.data ?? '') });
  }, [colIds, patch]);

  const remove = useCallback(async (id) => {
    setBusy(true);
    setError('');
    try {
      const res = await interDepotService.deletePosting(id);
      setToast(res.message || 'Removed.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  }, [load]);

  const onCellClicked = useCallback(([col, row], event) => {
    if (!canEdit) return;
    const r = filteredRef.current[row];
    if (!r) return;
    const id = colIds[col];
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'account') setPicker({ id: r.posting_id, field: 'account_code', ...pos });
    else if (id === 'cost_center') setPicker({ id: r.posting_id, field: 'cost_center_code', ...pos });
    else if (id === 'del') remove(r.posting_id);
  }, [colIds, canEdit, remove]);

  const onHeaderMenu = useCallback((colIndex, bounds) => {
    const colId = colIds[colIndex];
    const acc = accessors[colId];
    if (!acc) return;
    const options = [...new Set(sorted.map((r) => String(acc(r) ?? '')))].filter(Boolean).sort();
    setFilterUI({ colId, options, x: bounds ? bounds.x : 200, y: bounds ? bounds.y + bounds.height : 200 });
  }, [colIds, accessors, sorted]);

  const pickerOptions = useMemo(() => {
    if (!picker) return [];
    return picker.field === 'account_code'
      ? picks.accounts.map((a) => ({ code: a.code, description: a.description }))
      : picks.costCenters.map((c) => ({ code: c.code, description: c.description }));
  }, [picker, picks]);

  // ---- Writes ----------------------------------------------------------------
  const save = async () => {
    const changed = rules.filter((r) => dirty[r.posting_id]);
    if (!changed.length) return;
    setBusy(true);
    setError('');
    try {
      const res = await interDepotService.savePosting(changed);
      setToast(res.message || 'Saved.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!entry.site_code) { setError('Choose the site the lanes leave from.'); return; }
    if (!entry.account_code && !entry.cost_center_code) {
      setError('Set at least an account or a cost centre — a rule that says nothing would just shadow a broader one.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await interDepotService.savePosting([{
        ...entry,
        lane_id: entry.lane_id || null,
        carrier_type: entry.carrier_type || null,
      }]);
      setToast('Rule added.');
      setEntry(BLANK);
      setAdding(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const filterCount = Object.keys(filters || {}).length;

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to set inter-depot posting.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <button className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={() => navigate('/distribution')}>
          <ArrowLeft className="w-3.5 h-3.5" /> Distribution
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Inter-Depot Posting</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Where inter-depot transport lands in the ledger. Set a default per site, then narrow it by lane or by
              carrier type where it differs — the most specific rule wins.
            </p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button className={btn} onClick={() => setAdding((a) => !a)} disabled={busy}>
                <Plus className="w-3.5 h-3.5" /> Add rule
              </button>
              <button className={btnPrimary} onClick={save} disabled={busy || !Object.keys(dirty).length}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
            </div>
          )}
        </div>

        {!canEdit && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              Read-only — the posting catalogue needs an administrative role.
            </p>
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {toast && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <p className="m-0 text-xs text-emerald-700 dark:text-emerald-300">{toast}</p>
          </div>
        )}
        {!loading && uncovered.length > 0 && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              No rule for <strong>{uncovered.join(', ')}</strong> — lanes leaving those sites will cost correctly but
              post nowhere. Add a site default for each.
            </p>
          </div>
        )}

        {adding && canEdit && (
          <div className="mt-3 px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Site (lane origin)</label>
                <SearchSelect value={entry.site_code} onChange={(v) => setEntry((s) => ({ ...s, site_code: v, lane_id: '' }))}
                  options={picks.sites.map((s) => ({ code: s.site_code, name: s.site_name }))} placeholder="Choose a site…" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Lane (optional)</label>
                {/* `label`, not `code`+`name`: the lane's code is a uuid, and
                    SearchSelect renders code in front of name — which put an
                    identifier where the route should be. label replaces the
                    pair outright, so the option reads as the movement it is.
                    The uuid stays searchable, which costs nothing. */}
                <SearchSelect value={entry.lane_id} onChange={(v) => setEntry((s) => ({ ...s, lane_id: v }))}
                  options={lanesForSite(entry.site_code).map((l) => ({
                    code: l.lane_id,
                    label: `${l.origin_site_code} → ${l.dest_site_code}${l.lane_type === 'RETURNS' ? '  (returns)' : ''}`,
                    hint: l.dest_name,
                  }))} placeholder="All lanes" clearLabel="All lanes" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Carrier (optional)</label>
                <select className={field} value={entry.carrier_type} onChange={(e) => setEntry((s) => ({ ...s, carrier_type: e.target.value }))}>
                  {CARRIER_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">GL account</label>
                <SearchSelect value={entry.account_code} onChange={(v) => setEntry((s) => ({ ...s, account_code: v }))}
                  options={picks.accounts.map((a) => ({ code: a.code, name: a.description }))} placeholder="— none —" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Cost centre</label>
                <SearchSelect value={entry.cost_center_code} onChange={(v) => setEntry((s) => ({ ...s, cost_center_code: v }))}
                  options={picks.costCenters.map((c) => ({ code: c.code, name: c.description }))} placeholder="— none —" />
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button className={btnPrimary} onClick={add} disabled={busy}>Add rule</button>
              <button className={btn} onClick={() => { setAdding(false); setEntry(BLANK); }}>Cancel</button>
              <p className="m-0 ml-2 text-[11px] text-slate-400">
                Leave lane and carrier blank for the site default. Narrower rules beat it.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 max-h-full flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : sorted.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              No posting rules yet. Start with a default per site, then add exceptions.
            </p>
          ) : (
            <MantleDataGrid
              gridId="dist-transfer-posting"
              fitHeight
              fitHeightPadding={6}
              columns={gcols}
              rowCount={filtered.length}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              onHeaderMenuClick={onHeaderMenu}
              freezeColumns={1}
              hint="Click a GL Account or Cost Centre cell to pick one — neither is typed. Notes edit in place. ▾ on a heading filters, Ctrl+F searches. ✕ removes a rule and its lanes fall back to the next broadest one."
              toolbar={<>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {filtered.length} of {sorted.length} rule{sorted.length === 1 ? '' : 's'}
                  {Object.keys(dirty).length > 0 && ' · unsaved changes'}
                </span>
                {filterCount > 0 && (
                  <button className="text-[11px] font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-200" onClick={clearAll}>
                    Clear filters
                  </button>
                )}
              </>}
            />
          )}
        </div>

        <p className="m-0 text-[11px] text-slate-400 max-w-3xl">
          Resolution order, narrowest first: <strong>site + lane + carrier</strong> → <strong>site + lane</strong> →
          {' '}<strong>site + carrier</strong> → <strong>site default</strong>. A lane with no matching rule still costs
          correctly on the rating page; it simply has nowhere to post.
        </p>
      </div>

      {picker && (
        <PickerPopover
          anchor={{ x: picker.x, y: picker.y }}
          value={rules.find((r) => r.posting_id === picker.id)?.[picker.field] || ''}
          options={pickerOptions}
          placeholder="Search…"
          emptyText="Nothing matches."
          onPick={(code) => { patch(picker.id, { [picker.field]: code }); setPicker(null); }}
          onClear={() => { patch(picker.id, { [picker.field]: null }); setPicker(null); }}
          clearLabel="— none —"
          onClose={() => setPicker(null)}
        />
      )}

      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={{ x: filterUI.x, y: filterUI.y }}
          options={filterUI.options}
          filter={filters?.[filterUI.colId]}
          onApply={(f) => { setFilter(filterUI.colId, f); setFilterUI(null); }}
          onClose={() => setFilterUI(null)}
        />
      )}
    </div>
  );
}
